import type { EmployeeRow, TouchMonthRow, WorklistRow } from '@/data/types'
import { PERIOD_MONTHS } from '@/data/types'

/** DIVIDE() — returns null rather than dividing by zero, matching DAX. */
export function divide(n: number, d: number): number | null {
  return d === 0 ? null : n / d
}

const sum = <T,>(rows: readonly T[], f: (r: T) => number): number =>
  rows.reduce((a, r) => a + f(r), 0)

/* ---------------------------------------------------------------- core money */

export const billedClaims = (e: readonly EmployeeRow[]) => sum(e, (r) => r.claims)
export const claimLabor = (e: readonly EmployeeRow[]) => sum(e, (r) => r.claimLabor)
export const payroll = (e: readonly EmployeeRow[]) => sum(e, (r) => r.payroll)

/**
 * THE headline measure. A weighted ratio — SUM(labor) / SUM(claims).
 * Never an average of per-row rates: an average of averages lets a three-claim
 * employee move a department.
 */
export const costPerClaim = (e: readonly EmployeeRow[]) =>
  divide(claimLabor(e), billedClaims(e))

/**
 * Claim labor that actually landed on a claim.
 *
 * Measured by SUBTRACTING each person's unattributed labor, not by dropping people
 * with no claims. Attribution fails per claim, not per person, so the two are not the
 * same question: on the 2026-09-17 bundle 95 people carry attributed claims AND
 * $1,029,726 of labor that attributed to nothing. Filtering on `claims > 0` kept every
 * dollar those people carried and called it attributed, which understated the gap by
 * that $1.03M and made `attributedCostPerClaim` read lower than it is.
 *
 * Requires a hydrated dataset — see data/hydrate.ts.
 */
export const attributedClaimLabor = (e: readonly EmployeeRow[]) =>
  claimLabor(e) - unattributedClaimLabor(e)

export const attributedCostPerClaim = (e: readonly EmployeeRow[]) =>
  divide(attributedClaimLabor(e), billedClaims(e))

/**
 * The single largest finding in the data: about 37% of claim labor sits in paid months
 * that billed no claims (MEASURE-RULES §3.5). That is missing attribution, not idle staff.
 *
 * 280 of 742 people account for most of it by having no claims at all; the remaining
 * $1,029,726 is spread across 95 people who do.
 */
export const unattributedClaimLabor = (e: readonly Pick<EmployeeRow, 'unattributedLabor'>[]) =>
  sum(e, (r) => r.unattributedLabor)

/* --------------------------------------------------- throughput and quality */

export const resolvedClaims = (t: readonly TouchMonthRow[]) =>
  sum(t, (r) => r.resolvedClaims)

/**
 * UNCAPPED. Resolution lags billing, so anyone clearing a backlog exceeds 100% —
 * 110 of 275 people do. Do NOT clamp this to 1; the cap hides the best performers.
 */
export const closedClaimPct = (
  e: readonly EmployeeRow[],
  t: readonly TouchMonthRow[],
) => divide(resolvedClaims(t), billedClaims(e))

/**
 * The two halves of a collection rate, ready to pool: resolved charge over total
 * charge. Pool by adding both and dividing once — `poolCollection`.
 */
export interface CollectionBasis {
  resolved: number
  total: number
}

/**
 * One row's collection basis — a person, a person-month, or a reweighted person.
 *
 * Since 2026-10-05 a row's sums are a lagged WINDOW of bill months, not the row's own
 * bills (MEASURE-RULES §4.3, measures/collection.ts); person-months therefore pool
 * across people for one month and never across months.
 *
 * `Collected Percentage` is a DOLLAR ratio, SUM(resolved charge) / SUM(charge)
 * (MEASURE-CATALOG, MEASURE-RULES §4.3). Pooling the per-row rates weighted by billed
 * claims agrees with it only when everybody bills the same dollars per claim, so a
 * department or a team would not tie to Power BI. Bundles from 2026-09-30 carry the
 * two sums; this returns them.
 *
 * FALLBACK for bundles built before then, which carry only the rate: the rate times
 * `claims` over `claims` — the claim-weighted pooling the app used until 2026-09-30.
 * Wrong in a known direction rather than blank, and exact for a single row. It is
 * all-or-nothing per bundle (both fields arrive together), so the two bases never
 * mix in one sum.
 *
 * Null when the row has neither — no charge basis at all, which must not count as
 * zero (it would drag the pool toward 0% in proportion to what is missing).
 */
export function collectionBasis(
  r: {
    collectedPct: number | null
    charge?: number | null
    chargeResolved?: number | null
  },
  claims: number,
): CollectionBasis | null {
  if (r.charge != null && r.chargeResolved != null)
    return { resolved: r.chargeResolved, total: r.charge }
  if (r.collectedPct === null) return null
  return { resolved: r.collectedPct * claims, total: claims }
}

/** SUM(resolved) / SUM(total) over the rows that have a basis. Never a mean of rates. */
export function poolCollection(bases: Iterable<CollectionBasis | null>): number | null {
  let resolved = 0
  let total = 0
  for (const b of bases) {
    if (b === null) continue
    resolved += b.resolved
    total += b.total
  }
  return divide(resolved, total)
}

/** Add bases; null only when every one is null. */
export function addCollection(
  bases: Iterable<CollectionBasis | null>,
): CollectionBasis | null {
  let out: CollectionBasis | null = null
  for (const b of bases) {
    if (b === null) continue
    out = out === null ? { ...b } : { resolved: out.resolved + b.resolved, total: out.total + b.total }
  }
  return out
}

/**
 * Collection rate over a set of people: SUM(resolved charge) / SUM(charge), over the
 * rows that HAVE a basis. Treating a missing rate as zero drags the result toward zero
 * in proportion to how much is missing — the lakehouse bundle once carried no
 * collected % at all and it rendered as a confident "0.0%". Null when nothing is
 * known; the KPI card reads that as "no data".
 */
export const collectionRate = (e: readonly EmployeeRow[]) =>
  poolCollection(e.map((r) => collectionBasis(r, r.claims)))

/**
 * The touch-share denominator: the three rule-of-record buckets, which partition the
 * claims resolved in the month, billed any month (MEASURE-RULES.md §5.1–5.2, review
 * #397). On a bundle built since then it equals `resolvedClaims`; on an older one the
 * buckets are by bill month and do not, so it is still summed from the buckets.
 *
 * Never the no-touch claims past their payer type's age limit, which `applyAgedNoTouch`
 * moves out of `resolvedNoTouch` into `resolvedNoTouchAged`: they leave the 0% touch
 * share entirely, numerator and base (Rule 5.7, #695 as rewritten 2026-10-18). So with
 * a limit set the base is the resolved claims less the aged ones.
 */
export const touchBase = (r: TouchMonthRow) =>
  r.resolvedNoTouch + r.resolvedOneTouch + r.resolvedTwoPlusTouch
/** Claims with at least one touch — the base of touches per touched claim. */
export const touchedBase = (r: TouchMonthRow) => r.resolvedOneTouch + r.resolvedTwoPlusTouch

export const zeroTouchShare = (t: readonly TouchMonthRow[]) =>
  divide(sum(t, (r) => r.resolvedNoTouch), sum(t, touchBase))
export const oneTouchShare = (t: readonly TouchMonthRow[]) =>
  divide(sum(t, (r) => r.resolvedOneTouch), sum(t, touchBase))
export const twoPlusShare = (t: readonly TouchMonthRow[]) =>
  divide(sum(t, (r) => r.resolvedTwoPlusTouch), sum(t, touchBase))

/** §5.5: an average over TOUCHED claims — untouched ones are not in the base. */
export const touchesPerTouchedClaim = (t: readonly TouchMonthRow[]) =>
  divide(sum(t, (r) => r.resolvedTouches), sum(t, touchedBase))

/**
 * RESOLVED denominator. This is NOT cost per claim — at org level it is $50.26
 * against the billed-basis $35.32. Keeping the two apart is the most important
 * discipline in this codebase.
 */
export const costPerResolvedClaim = (t: readonly TouchMonthRow[]) =>
  divide(
    sum(t, (r) => r.costPerResolvedClaim * r.resolvedClaims),
    resolvedClaims(t),
  )

/* ------------------------------------------------------------ volume, backlog */

export const billedClaimsPerMonth = (e: readonly EmployeeRow[]) =>
  divide(billedClaims(e), PERIOD_MONTHS)

export const openClaims = (w: readonly WorklistRow[]) => sum(w, (r) => r.openClaims)
/**
 * The worklist's `claimsNotWorked`. In a lakehouse bundle that is the snapshot's pile
 * less what its owner touched in the snapshot's month — not worked this month, not
 * never worked (MEASURE-RULES §5.2a). The zip export filled the same field from the
 * prototype's own "Claims Not Worked" column.
 */
export const claimsNotWorked = (w: readonly WorklistRow[]) => sum(w, (r) => r.claimsNotWorked)
export const notWorkedPct = (w: readonly WorklistRow[]) =>
  divide(claimsNotWorked(w), openClaims(w))

/**
 * The open pile by when ANYONE last touched each claim, counted back from the snapshot
 * date (`Dataset.worklistAsOf`, day 0). The four partition the pile: they add up to
 * `openClaims` exactly (MEASURE-RULES §5.2a).
 *
 * `neverWorked` is no touch at all since the touch fact begins (`touchHistoryFrom`) —
 * not "not worked this month", which also holds claims worked in an earlier month.
 */
export interface WorklistAge {
  open: number
  neverWorked: number
  /** Last touched 0–14 days before the snapshot. */
  last14: number
  /** 15–30 days before. */
  from15to30: number
  /** More than 30 days before. */
  over30: number
}

/**
 * One worklist row's age buckets, or null when the bundle predates them. A row with
 * the fields absent is unknown, never "nothing is never worked".
 */
export function worklistAge(r: WorklistRow | undefined): WorklistAge | null {
  if (!r || r.neverWorked == null) return null
  return {
    open: r.openClaims,
    neverWorked: r.neverWorked,
    last14: r.workedLast14 ?? 0,
    from15to30: r.worked15to30 ?? 0,
    over30: r.workedOver30 ?? 0,
  }
}

/** Age buckets pooled across rows: counts summed, so shares weight by pile size. */
export function pooledWorklistAge(rows: readonly (WorklistAge | null)[]): WorklistAge {
  const t: WorklistAge = { open: 0, neverWorked: 0, last14: 0, from15to30: 0, over30: 0 }
  for (const a of rows) {
    if (!a) continue
    t.open += a.open
    t.neverWorked += a.neverWorked
    t.last14 += a.last14
    t.from15to30 += a.from15to30
    t.over30 += a.over30
  }
  return t
}

/** The buckets the pages show: the two within-30-day ones are one (Review Hub #893). */
export type AgeBucketKey = 'last30' | 'over30' | 'neverWorked'

/**
 * The three buckets, in the one order and with the one set of labels every surface uses:
 * a count and its share of the pile for each, and nothing else about recency. Shayna,
 * 2026-10-01: "we need four categories … count and %. This should carry through
 * throughout"; 2026-10-21 (#893): consolidate the 14-day and 15–30-day rows, "at the end of
 * it all we just need a % of claims worked under 30 days". Still no overlapping windows and
 * no month-based rows beside them: the three add up to the pile.
 */
export const AGE_BUCKETS: readonly { key: AgeBucketKey; label: string; def: string }[] = [
  { key: 'last30', label: 'Worked in last 30 days', def: 'workedLast30' },
  { key: 'over30', label: 'Last worked over 30 days ago', def: 'workedOver30' },
  { key: 'neverWorked', label: 'Never worked', def: 'neverWorked' },
]

/** One bucket's count: Worked in last 30 days is the 0–14 and 15–30-day touches together. */
export const ageCount = (a: WorklistAge, key: AgeBucketKey) => (key === 'last30' ? a.last14 + a.from15to30 : a[key])

/** One bucket ÷ the pile; null for an empty pile, never 0%. */
export const ageShare = (a: WorklistAge, key: AgeBucketKey) => divide(ageCount(a, key), a.open)
